import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  ContractorPortalFetchPaymentMethod,
  ContractorPortalSendCode,
  ContractorPortalUpdateEmail,
  ContractorPortalUpdateEmailSendCode,
  ContractorPortalUpdatePhone,
  ContractorPortalUpdatePhoneSendCode,
  ContractorPortalVerifyCode,
} from 'src/constants/actionTypes'
import { ApiService, callContractorPortalSecureApi } from '../api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { ContractorPortalPaymentMethod } from 'src/interfaces/contractorPortal'
import { captureSentryError } from 'src/utils/sentry'

function* onContractorPortalFetchPaymentMethod(action: AsyncAction<void>) {
  try {
    const { payload }: { payload: ContractorPortalPaymentMethod } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalFetchPaymentMethod,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(ContractorPortalFetchPaymentMethod.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalFetchPaymentMethod] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to fetch payment method'

    yield put(ContractorPortalFetchPaymentMethod.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onContractorPortalSendCode(action: AsyncAction<void>) {
  try {
    const { payload }: { payload: any } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalSendCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
      },
    )

    yield put(ContractorPortalSendCode.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalSendCode] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to send code'

    yield put(ContractorPortalSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalVerifyCodeRequest {
  code: string
}

function* onContractorPortalVerifyCode(action: AsyncAction<ContractorPortalVerifyCodeRequest>) {
  try {
    const { code } = action.payload

    const { payload }: { payload: { token: string } } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalVerifyCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: { code },
      },
    )

    yield put(ContractorPortalVerifyCode.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalVerifyCode] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to verify code'

    yield put(ContractorPortalVerifyCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdatePhoneSendCodeRequest {
  phone: string
  token: string
}

function* onContractorPortalUpdatePhoneSendCode(
  action: AsyncAction<ContractorPortalUpdatePhoneSendCodeRequest>,
) {
  try {
    const { phone, token } = action.payload

    yield call(callContractorPortalSecureApi, Endpoints.ContractorPortalUpdatePhoneSendCode, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      query: { token },
      body: { phone },
    })

    yield put(ContractorPortalUpdatePhoneSendCode.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdatePhoneSendCode] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to send code'

    yield put(ContractorPortalUpdatePhoneSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdatePhoneRequest {
  token: string
  code: string
  phone: string
}

function* onContractorPortalUpdatePhone(action: AsyncAction<ContractorPortalUpdatePhoneRequest>) {
  try {
    const { token, code, phone } = action.payload

    yield call(callContractorPortalSecureApi, Endpoints.ContractorPortalUpdatePhone, {
      apiService: ApiService.Paraworks,
      method: 'PUT',
      query: { token },
      body: { code, phone },
    })

    yield put(ContractorPortalUpdatePhone.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdatePhone] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to update phone number'

    yield put(ContractorPortalUpdatePhone.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdateEmailSendCodeRequest {
  email: string
  token: string
}

function* onContractorPortalUpdateEmailSendCode(
  action: AsyncAction<ContractorPortalUpdateEmailSendCodeRequest>,
) {
  try {
    const { email, token } = action.payload

    const { payload }: { payload: any } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalUpdateEmailSendCode,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        query: { token },
        body: { email },
      },
    )

    yield put(ContractorPortalUpdateEmailSendCode.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdateEmailSendCode] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to send code'

    yield put(ContractorPortalUpdateEmailSendCode.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorPortalUpdateEmailRequest {
  token: string
  code: string
  email: string
}

function* onContractorPortalUpdateEmail(action: AsyncAction<ContractorPortalUpdateEmailRequest>) {
  try {
    const { token, code, email } = action.payload

    const { payload }: { payload: any } = yield call(
      callContractorPortalSecureApi,
      Endpoints.ContractorPortalUpdateEmail,
      {
        apiService: ApiService.Paraworks,
        method: 'PUT',
        query: { token },
        body: { code, email },
      },
    )

    yield put(ContractorPortalUpdateEmail.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.contractorPortal.onContractorPortalUpdateEmail] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to update email address'

    yield put(ContractorPortalUpdateEmail.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* contractorPortalDriverDataSaga() {
  yield takeEvery(
    ContractorPortalFetchPaymentMethod.type.REQUEST,
    onContractorPortalFetchPaymentMethod,
  )
  yield takeEvery(ContractorPortalSendCode.type.REQUEST, onContractorPortalSendCode)
  yield takeEvery(ContractorPortalVerifyCode.type.REQUEST, onContractorPortalVerifyCode)
  yield takeEvery(
    ContractorPortalUpdateEmailSendCode.type.REQUEST,
    onContractorPortalUpdateEmailSendCode,
  )
  yield takeEvery(ContractorPortalUpdateEmail.type.REQUEST, onContractorPortalUpdateEmail)
  yield takeEvery(
    ContractorPortalUpdatePhoneSendCode.type.REQUEST,
    onContractorPortalUpdatePhoneSendCode,
  )
  yield takeEvery(ContractorPortalUpdatePhone.type.REQUEST, onContractorPortalUpdatePhone)
}
